import { create } from 'zustand';
import { apiFetch } from '@/lib/api';
import { AuthService } from '../services/AuthService';
import { resetSessionScopedState } from '@/lib/sessionReset';
import { applyServerThemePreference, resetThemeOnLogout } from '@/lib/theme';
import type { User } from '@/types/user';

type AuthState = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
};

type AuthActions = {
  // register: (email: string, password: string, fullName: string, orgName: string) => Promise<void>; // Registration disabled
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: (skipRedirect?: boolean) => Promise<void>;
};

const svc = new AuthService();

// Deduplicate in-flight requests to /api/auth/me (e.g., StrictMode double effects)
let ongoingMeRequest: Promise<User> | null = null;

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  user: null,
  isLoading: false,
  error: null,

  // Registration method removed - account creation is disabled

  login: async (email, password, rememberMe) => {
    try {
      set({ isLoading: true, error: null });
      const me = await svc.login({ email, password, rememberMe });
      // Wipe any residue from a previous session before writing the new user.
      // Guards against crashed/failed logouts that never ran their cleanup.
      // Also clears the React Query cache so stale permissions don't bleed
      // across user switches (B12).
      resetSessionScopedState();
      // B10: Apply server-persisted theme preference immediately on login so
      // the next browser session doesn't inherit the previous user's dark mode.
      applyServerThemePreference(me.themePreference ?? 'system');
      set({ user: me, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Login failed', isLoading: false });
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await svc.logout();
    } catch (e) {
      // A failed backend logout should not leave the UI half-logged-in.
      // Log but still run the client-side reset below.
      if (process.env.NODE_ENV === 'development') console.warn('[authStore] svc.logout() failed, continuing with client reset', e);
    }
    // B10: Reset theme to 'system' on logout so next user on the same browser
    // doesn't inherit the previous user's dark-mode preference.
    resetThemeOnLogout();
    // B12: Full cache clear ensures nav/permissions queries don't bleed across
    // user sessions. queryClient.clear() is called inside resetSessionScopedState.
    resetSessionScopedState();
    set({ user: null, isLoading: false, error: null });
  },

  fetchMe: async (skipRedirect = false) => {
    // If a request is already in-flight, await it instead of issuing another
    if (ongoingMeRequest) {
      try {
        await ongoingMeRequest;
      } finally {
        set({ isLoading: false });
      }
      return;
    }

    try {
      set({ isLoading: true, error: null });
      ongoingMeRequest = svc.me(skipRedirect);
      const me = await ongoingMeRequest;
      // B10: Apply server-persisted theme preference on every /api/auth/me
      // response — covers fetchMe() calls from ProtectedRoute, ProfilePage, etc.
      applyServerThemePreference(me.themePreference ?? 'system');
      set({ user: me, isLoading: false });
    } catch (e) {
      set({ user: null, isLoading: false });
    } finally {
      ongoingMeRequest = null;
    }
  },
}));


